/**
 * The cart lines for a monogrammed product, in the legacy theme's format,
 * which order handling still reads: the product and the monogram's pricing
 * variant as two lines, linked by `_uuid` and carrying the same properties.
 * The monogram's line adds the type's cart note as `Note`. The mini cart
 * (snippet.cart-content.liquid) pairs the lines back up.
 */
import {
  GOLD_FOIL,
  HAND_PAINTED,
  fontFor,
  monogramPrice,
  monogramText,
  placementFor,
  pricingVariant,
} from "./monogram-options.js";

/** The shopper's currency per unit of the store's. */
export const currencyRate = () => Number(window.Shopify?.currency?.rate) || 1;

const upper = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

/**
 * @param {Object} config - MonogramData JSON
 * @param {Object} selection - A normalised selection
 * @param {{ uuid: string, sku: string }} line
 * @returns {Object<string, string>}
 */
export function monogramProperties(config, selection, { uuid, sku }) {
  const properties = {
    _uuid: uuid,
    Item: "Final Sale",
    _Product: config.productTitle,
  };

  if (selection.type === GOLD_FOIL) {
    properties.Font = upper(fontFor(config, selection)?.name);
  }

  properties.Text = upper(monogramText(selection));
  properties.Placement = upper(placementFor(config, selection)?.title);

  if (selection.type === HAND_PAINTED) {
    const colors = config.handPainted?.colors ?? [];
    const colorLabel = (id) => colors.find((color) => color.id === id)?.label;
    const secondColor = colorLabel(selection.secondColorId);

    properties.Size = selection.size === 2 ? "TWO INCH" : "ONE INCH";
    properties.FirstColor = upper(colorLabel(selection.firstColorId));
    properties.SecondColor = secondColor ? upper(secondColor) : "NONE";
    properties.DropShadow = secondColor ? "YES" : "NO";
  }

  properties._SKU = sku ?? "";

  return properties;
}

/**
 * @param {Object} options
 * @param {Object} options.config - MonogramData JSON
 * @param {Object} options.selection - A normalised selection
 * @param {{ id: number, price: number, sku: string }} options.variant - The
 *   product variant being monogrammed
 * @param {Object<string, string>} [options.properties] - The product form's
 *   own line item properties, for the product's line
 * @param {string} options.uuid
 * @param {number} [options.rate] - The shopper's currency per unit of the store's
 * @returns {Array<Object>|null} Items for /cart/add, or null when no
 *   available pricing variant matches the monogram's price
 */
export function monogramCartItems({
  config,
  selection,
  variant,
  properties = {},
  uuid,
  rate = 1,
}) {
  const price = monogramPrice(selection, variant.price, rate);
  const monogramVariant = pricingVariant(config, selection.type, price);
  if (!monogramVariant?.available) return null;

  const monogram = monogramProperties(config, selection, {
    uuid,
    sku: variant.sku,
  });
  const cartNote = config[selection.type]?.cartNote;

  return [
    {
      id: variant.id,
      quantity: 1,
      properties: { ...properties, ...monogram },
    },
    {
      id: monogramVariant.id,
      quantity: 1,
      properties: cartNote ? { ...monogram, Note: cartNote } : monogram,
    },
  ];
}
